import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../context/SettingsContext';
import { updateProfile, changePassword } from '../../services/auth';
import WeatherLocationCard from './WeatherLocationCard';
import { useLook } from '../../hooks/useLook';

interface Props {
  user: any;
  onThemeToggle: () => void;
  isDark: boolean;
  onUserUpdated: (patch: { name: string | null }) => void;
}

type Tab = 'profile' | 'preferences';

export default function SettingsPage({ user, onThemeToggle, isDark, onUserUpdated }: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('profile');

  const tabs: { key: Tab; icon: string; label: string }[] = [
    { key: 'profile',     icon: '👤', label: 'Profile' },
    { key: 'preferences', icon: '🎛', label: 'Preferences' },
  ];

  return (
    <div className="page" style={{ padding: 0, display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left tab rail */}
      <div style={{ width: 200, borderRight: '1px solid var(--border)', background: 'var(--bg)', flexShrink: 0, padding: '24px 10px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 8px 12px' }}>
          {t('settings.title')}
        </div>
        {tabs.map(tab => (
          <div
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
              borderRadius: 8, cursor: 'pointer', marginBottom: 2,
              background: activeTab === tab.key ? 'var(--white)' : 'transparent',
              boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.12s',
            }}
            onMouseEnter={e => { if (activeTab !== tab.key) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
            onMouseLeave={e => { if (activeTab !== tab.key) e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ fontSize: 15 }}>{tab.icon}</span>
            <span style={{ fontSize: 13, color: activeTab === tab.key ? 'var(--green-dk)' : 'var(--dark)', fontWeight: activeTab === tab.key ? 600 : 400 }}>
              {tab.label}
            </span>
          </div>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
        {activeTab === 'profile'     && <ProfileTab user={user} onUserUpdated={onUserUpdated} />}
        {activeTab === 'preferences' && <PreferencesTab onThemeToggle={onThemeToggle} isDark={isDark} user={user} />}
      </div>
    </div>
  );
}

// ── Profile ──────────────────────────────────────────────────────
function ProfileTab({ user, onUserUpdated }: { user: any; onUserUpdated: (patch: { name: string | null }) => void }) {
  const { t } = useTranslation();
  const initials = (user?.name || user?.email || 'U').slice(0, 2).toUpperCase();

  const [name, setName]           = useState(user?.name ?? '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSaved, setNameSaved]   = useState(false);
  const [nameError, setNameError]   = useState('');

  const handleSaveName = async () => {
    setNameSaving(true);
    setNameError('');
    try {
      const { user: updated } = await updateProfile(name);
      setName(updated.name ?? '');
      onUserUpdated({ name: updated.name });
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2000);
    } catch (err: any) {
      setNameError(err.response?.data?.error ?? 'Could not save your name.');
    } finally {
      setNameSaving(false);
    }
  };

  const [pw, setPw]               = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving]   = useState(false);
  const [pwSaved, setPwSaved]     = useState(false);
  const [pwError, setPwError]     = useState('');

  const handleSavePassword = async () => {
    setPwError('');
    if (pw.next !== pw.confirm) {
      setPwError('New passwords do not match.');
      return;
    }
    setPwSaving(true);
    try {
      await changePassword(pw.current, pw.next);
      setPw({ current: '', next: '', confirm: '' });
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 2000);
    } catch (err: any) {
      setPwError(err.response?.data?.error ?? 'Could not change your password.');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <PageHeader title={t('settings.tabs.profile')} subtitle={t('settings.profileSubtitle')} />

      {/* Avatar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: 'var(--green)', flexShrink: 0 }}>
          {initials}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{user?.name || user?.email || 'User'}</div>
          <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>{user?.role?.replace('_', ' ') || 'User'}</div>
        </div>
      </div>

      <Card title={t('settings.profile.personalInfo')}>
        <Field label={t('settings.profile.name')}><input className="settings-input" value={name} onChange={e => setName(e.target.value)} placeholder={t('settings.profile.yourName')} /></Field>
        <Field label={t('settings.profile.email')}><input className="settings-input" defaultValue={user?.email ?? ''} disabled style={{ opacity: 0.6 }} /></Field>
      </Card>
      {nameError && <div className="error-message" style={{ marginBottom: 12 }}>{nameError}</div>}
      <SaveButton saved={nameSaved} saving={nameSaving} onClick={handleSaveName} />

      <div style={{ marginTop: 32 }}>
        <Card title={t('settings.profile.changePassword')}>
          <Field label={t('settings.profile.currentPassword')}><input className="settings-input" type="password" value={pw.current} onChange={e => setPw({ ...pw, current: e.target.value })} placeholder="••••••••" /></Field>
          <Field label={t('settings.profile.newPassword')}><input className="settings-input" type="password" value={pw.next} onChange={e => setPw({ ...pw, next: e.target.value })} placeholder={t('settings.profile.minChars')} /></Field>
          <Field label={t('settings.profile.confirmPassword')}><input className="settings-input" type="password" value={pw.confirm} onChange={e => setPw({ ...pw, confirm: e.target.value })} placeholder={t('settings.profile.repeatPassword')} /></Field>
        </Card>
        {pwError && <div className="error-message" style={{ marginBottom: 12 }}>{pwError}</div>}
        <SaveButton
          saved={pwSaved}
          saving={pwSaving}
          disabled={!pw.current || !pw.next || !pw.confirm}
          onClick={handleSavePassword}
          label={t('settings.profile.changePassword')}
        />
      </div>
    </div>
  );
}

// ── Preferences ──────────────────────────────────────────────────
function PreferencesTab({ onThemeToggle, isDark, user }: { onThemeToggle: () => void; isDark: boolean; user?: any }) {
  const { esApple, alternar: alternarLook } = useLook();
  const { settings, update } = useSettings();
  const { i18n, t } = useTranslation() as any;
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    i18n.changeLanguage(settings.language);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <PageHeader title={t('settings.preferences')} subtitle={t('settings.preferencesSubtitle')} />

      <Card title={t('settings.units')}>
        <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>{t('settings.tempUnit')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['C', 'F'] as const).map(u => (
              <button
                key={u}
                onClick={() => update({ tempUnit: u })}
                style={{
                  padding: '8px 24px', borderRadius: 8, border: '1.5px solid',
                  borderColor: settings.tempUnit === u ? 'var(--green)' : 'var(--border)',
                  background: settings.tempUnit === u ? 'var(--green-bg)' : 'var(--white)',
                  color: settings.tempUnit === u ? 'var(--green-dk)' : 'var(--gray)',
                  fontWeight: settings.tempUnit === u ? 600 : 400,
                  fontSize: 14, cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                °{u}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 8 }}>
            {settings.tempUnit === 'C' ? 'Displaying in Celsius — e.g. 18.5°C' : 'Displaying in Fahrenheit — e.g. ' + Math.round(18.5 * 9/5 + 32) + '°F'}
          </div>
        </div>

        <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>{t('settings.dateFormat')}</div>
          <select
            className="settings-input"
            value={settings.dateFormat}
            onChange={e => update({ dateFormat: e.target.value as any })}
            style={{ maxWidth: 220 }}
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY (EU)</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
          </select>
        </div>

        <div style={{ padding: '14px 0' }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>{t('settings.timeZone')}</div>
          <select
            className="settings-input"
            value={settings.timezone}
            onChange={e => update({ timezone: e.target.value })}
            style={{ maxWidth: 280 }}
          >
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="America/Mexico_City">Mexico City (CST)</option>
            <option value="America/Bogota">Colombia (COT)</option>
            <option value="UTC">UTC</option>
          </select>
        </div>
      </Card>

      <Card title={t('settings.language')}>
        <div style={{ padding: '12px 0' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {([['en', '🇺🇸 English'], ['es', '🇲🇽 Español']] as const).map(([code, label]) => (
              <button
                key={code}
                onClick={() => update({ language: code })}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: '1.5px solid',
                  borderColor: settings.language === code ? 'var(--green)' : 'var(--border)',
                  background: settings.language === code ? 'var(--green-bg)' : 'var(--white)',
                  color: settings.language === code ? 'var(--green-dk)' : 'var(--gray)',
                  fontWeight: settings.language === code ? 600 : 400,
                  fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card title={t('settings.system.appearance')}>
        <ToggleRow
          label={t('settings.system.darkMode')}
          description={t('settings.system.darkModeDesc')}
          value={isDark}
          onChange={onThemeToggle}
        />
        {/* Eje aparte del claro/oscuro: se combinan, no se excluyen.
            Ver hooks/useLook.ts para por qué van en atributos distintos. */}
        <ToggleRow
          label={t('settings.appleLook')}
          description={t('settings.appleLookHint')}
          value={esApple}
          onChange={alternarLook}
        />
      </Card>

      {/* Ubicación del clima. Va aquí aunque se guarde en el servidor y no
          en el navegador como el resto de esta pestaña: para quien la usa
          es un ajuste más, y separarla en otra pantalla sólo la escondería. */}
      <WeatherLocationCard rol={user?.role} />

      <SaveButton saved={saved} onClick={handleSave} />
    </div>
  );
}

// ── Shared sub-components ────────────────────────────────────────
function PageHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--dark)', marginBottom: 4 }}>{title}</h1>
      <p style={{ fontSize: 13, color: 'var(--gray)' }}>{subtitle}</p>
      <div style={{ height: 1, background: 'var(--border)', marginTop: 16 }} />
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>{title}</div>
      <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, padding: '4px 18px' }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <label style={{ fontSize: 12, color: 'var(--gray)', display: 'block', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

function ToggleRow({ label, description, value, onChange }: { label: string; description: string; value: boolean; onChange: () => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0', borderBottom: '1px solid var(--border)' }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>{description}</div>
      </div>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: () => void }) {
  return (
    <div onClick={onChange} style={{ width: 42, height: 24, borderRadius: 12, background: value ? 'var(--green)' : 'var(--border)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: 16 }}>
      <div style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
    </div>
  );
}

function SaveButton({ saved, saving, disabled, label, onClick }: {
  saved: boolean; saving?: boolean; disabled?: boolean; label?: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={saving || disabled}
      className="primary-button"
      style={{ padding: '9px 28px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: (saving || disabled) ? 0.6 : 1 }}
    >
      {saved ? '✓ Saved' : saving ? 'Saving...' : (label ?? 'Save Changes')}
    </button>
  );
}
